import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { 
  Plus, 
  TrendingUp, 
  TrendingDown, 
  ArrowUpRight, 
  ArrowDownRight,
  FileText,
  FileInput,
  AlertTriangle,
  Calendar,
  Info
} from "lucide-react";
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  Legend,
  ReferenceLine
} from "recharts";

const formatCurrency = (value) => {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR'
  }).format(value);
};

const MOVEMENT_CATEGORIES = [
  "Servizi",
  "Vendite",
  "Incassi Crediti",
  "Fornitori",
  "Personale",
  "Utenze",
  "Tasse",
  "Altro"
];

const Cashflow = () => {
  const { getAuthHeaders } = useAuth();
  const [cashflow, setCashflow] = useState(null);
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [viewMode, setViewMode] = useState("30");
  const [includeInvoices, setIncludeInvoices] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [formData, setFormData] = useState({
    description: "",
    amount: "",
    type: "income",
    date: new Date().toISOString().split("T")[0],
    category: ""
  });

  useEffect(() => {
    fetchData();
  }, [includeInvoices]);

  const fetchData = async () => {
    try {
      const [cashflowRes, movementsRes] = await Promise.all([
        axios.get(`${API}/cashflow?include_invoices=${includeInvoices}`, getAuthHeaders()),
        axios.get(`${API}/movements`, getAuthHeaders())
      ]);
      setCashflow(cashflowRes.data);
      setMovements(movementsRes.data);
    } catch (error) {
      toast.error("Errore nel caricamento dati");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        amount: parseFloat(formData.amount)
      };

      await axios.post(`${API}/movements`, data, getAuthHeaders());
      toast.success("Movimento aggiunto");

      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error("Errore nel salvataggio");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminare questo movimento?")) return;
    try {
      await axios.delete(`${API}/movements/${id}`, getAuthHeaders());
      toast.success("Movimento eliminato");
      fetchData();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const resetForm = () => {
    setFormData({
      description: "",
      amount: "",
      type: "income",
      date: new Date().toISOString().split("T")[0],
      category: ""
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  const displayData = cashflow?.data?.slice(0, parseInt(viewMode)) || [];
  
  // Find days with invoices for highlighting
  const daysWithInvoices = displayData.filter(d => d.invoices && d.invoices.length > 0);
  
  // Group data by week for better visualization
  const weeklyData = [];
  for (let i = 0; i < displayData.length; i += 7) {
    const weekSlice = displayData.slice(i, i + 7);
    if (weekSlice.length > 0) {
      weeklyData.push({
        week: `Sett. ${Math.floor(i / 7) + 1}`,
        income: weekSlice.reduce((sum, d) => sum + (d.total_income || d.income || 0), 0),
        expense: weekSlice.reduce((sum, d) => sum + (d.total_expense || d.expense || 0), 0),
        invoice_income: weekSlice.reduce((sum, d) => sum + (d.invoice_income || 0), 0),
        invoice_expense: weekSlice.reduce((sum, d) => sum + (d.invoice_expense || 0), 0),
        balance: weekSlice[weekSlice.length - 1].balance
      });
    }
  }

  // Custom tooltip for chart
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    
    const dayData = displayData.find(d => d.date === label);
    const hasInvoices = dayData?.invoices && dayData.invoices.length > 0;
    
    return (
      <div className="bg-white p-4 rounded-xl shadow-lg border-0">
        <p className="font-semibold text-[#1D1D1F] mb-2">
          {new Date(label).toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}
        </p>
        <div className="space-y-1 text-sm">
          <p className="text-[#34C759]">Entrate: {formatCurrency(dayData?.total_income || 0)}</p>
          <p className="text-[#FF3B30]">Uscite: {formatCurrency(dayData?.total_expense || 0)}</p>
          <p className="text-[#007AFF] font-semibold">Saldo: {formatCurrency(dayData?.balance || 0)}</p>
        </div>
        {hasInvoices && (
          <div className="mt-3 pt-3 border-t border-[#E5E5E5]">
            <p className="text-xs font-semibold text-[#86868B] mb-2">FATTURE IN SCADENZA:</p>
            {dayData.invoices.map((inv, i) => (
              <div key={i} className="flex items-center justify-between text-xs py-1">
                <span className={inv.type === 'income' ? 'text-[#34C759]' : 'text-[#FF3B30]'}>
                  {inv.type === 'income' ? inv.cliente : inv.fornitore} ({inv.numero})
                </span>
                <span className="font-medium">
                  {inv.type === 'income' ? '+' : '-'}{formatCurrency(inv.amount)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="animate-fade-in" data-testid="cashflow-page">
      <div className="page-header flex items-start justify-between">
        <div>
          <h1 className="page-title">Cashflow 60 Giorni</h1>
          <p className="page-subtitle">Proiezione flusso di cassa con fatture integrate</p>
        </div>
        <div className="flex gap-3 items-center">
          {/* Include Invoices Toggle */}
          <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-xl border border-[#E5E5E5]">
            <FileText className="w-4 h-4 text-[#86868B]" />
            <Label htmlFor="include-invoices" className="text-sm text-[#1D1D1F]">
              Includi Fatture
            </Label>
            <Switch
              id="include-invoices"
              checked={includeInvoices}
              onCheckedChange={setIncludeInvoices}
              data-testid="include-invoices-toggle"
            />
          </div>
          
          <Select value={viewMode} onValueChange={setViewMode}>
            <SelectTrigger className="w-36 h-11 rounded-xl border-[#D2D2D7]" data-testid="view-mode-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white rounded-xl border-0 shadow-lg">
              <SelectItem value="30">30 Giorni</SelectItem>
              <SelectItem value="60">60 Giorni</SelectItem>
            </SelectContent>
          </Select>
          
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="bg-[#007AFF] hover:bg-[#0071E3] text-white rounded-full px-6"
                data-testid="add-movement-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nuovo Movimento
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white rounded-2xl border-0 shadow-2xl sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle className="text-xl font-semibold text-[#1D1D1F]">
                  Nuovo Movimento di Cassa
                </DialogTitle>
                <DialogDescription>
                  Aggiungi un movimento manuale al cashflow
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-[#1D1D1F]">Descrizione</Label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Es: Pagamento fornitore"
                    className="h-12 rounded-xl border-[#D2D2D7]"
                    data-testid="movement-description"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-[#1D1D1F]">Tipo</Label>
                    <Select
                      value={formData.type}
                      onValueChange={(value) => setFormData({ ...formData, type: value })}
                    >
                      <SelectTrigger className="h-12 rounded-xl border-[#D2D2D7]" data-testid="movement-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white rounded-xl border-0 shadow-lg">
                        <SelectItem value="income">
                          <span className="flex items-center gap-2">
                            <ArrowUpRight className="w-4 h-4 text-[#34C759]" />
                            Entrata
                          </span>
                        </SelectItem>
                        <SelectItem value="expense">
                          <span className="flex items-center gap-2">
                            <ArrowDownRight className="w-4 h-4 text-[#FF3B30]" />
                            Uscita
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#1D1D1F]">Importo (€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      placeholder="0,00"
                      className="h-12 rounded-xl border-[#D2D2D7]"
                      data-testid="movement-amount"
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-[#1D1D1F]">Data</Label>
                    <Input
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      className="h-12 rounded-xl border-[#D2D2D7]"
                      data-testid="movement-date"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#1D1D1F]">Categoria</Label>
                    <Select
                      value={formData.category}
                      onValueChange={(value) => setFormData({ ...formData, category: value })}
                    >
                      <SelectTrigger className="h-12 rounded-xl border-[#D2D2D7]" data-testid="movement-category">
                        <SelectValue placeholder="Seleziona" />
                      </SelectTrigger>
                      <SelectContent className="bg-white rounded-xl border-0 shadow-lg">
                        {MOVEMENT_CATEGORIES.map(cat => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsDialogOpen(false)}
                    className="flex-1 h-12 rounded-full border-[#D2D2D7]"
                  >
                    Annulla
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 h-12 bg-[#007AFF] hover:bg-[#0071E3] text-white rounded-full"
                    data-testid="save-movement-btn"
                  >
                    Salva
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Invoice Integration Banner */}
      {includeInvoices && cashflow?.pending_invoices && (
        <div className="mb-6 p-4 bg-gradient-to-r from-[#007AFF]/5 to-[#34C759]/5 rounded-2xl border border-[#007AFF]/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#007AFF]/10 rounded-xl flex items-center justify-center">
                <Info className="w-5 h-5 text-[#007AFF]" />
              </div>
              <div>
                <p className="font-semibold text-[#1D1D1F]">Fatture Integrate nel Cashflow</p>
                <p className="text-sm text-[#86868B]">
                  Le scadenze delle fatture sono incluse nella proiezione
                </p>
              </div>
            </div>
            <div className="flex gap-6">
              <div className="text-center">
                <p className="text-xs text-[#86868B] uppercase">Fatture Attive</p>
                <p className="text-lg font-semibold text-[#34C759]">
                  +{formatCurrency(cashflow.pending_invoices.total_attive)}
                </p>
                <p className="text-xs text-[#86868B]">{cashflow.pending_invoices.attive} fatture</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-[#86868B] uppercase">Fatture Passive</p>
                <p className="text-lg font-semibold text-[#FF3B30]">
                  -{formatCurrency(cashflow.pending_invoices.total_passive)}
                </p>
                <p className="text-xs text-[#86868B]">{cashflow.pending_invoices.passive} fatture</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Critical Days Warning */}
      {cashflow?.critical_days > 0 && (
        <div className="mb-6 p-4 bg-[#FF3B30]/5 rounded-2xl border border-[#FF3B30]/20">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#FF3B30]/10 rounded-xl flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-[#FF3B30]" />
            </div>
            <div>
              <p className="font-semibold text-[#FF3B30]">Attenzione: Saldo Negativo Previsto</p>
              <p className="text-sm text-[#86868B]">
                Il saldo diventerà negativo dal {new Date(cashflow.first_critical_day).toLocaleDateString('it-IT')} ({cashflow.critical_days} giorni critici)
              </p>
            </div>
          </div>
        </div>
      )}

      {/* KPI Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <Card className="stat-card card-hover">
          <CardContent className="pt-6">
            <p className="stat-card-label">Saldo Attuale</p>
            <p className="stat-card-value">{formatCurrency(cashflow?.current_balance || 0)}</p>
          </CardContent>
        </Card>
        
        <Card className="stat-card card-hover">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-[#34C759]" />
              <p className="stat-card-label">Entrate Totali</p>
            </div>
            <p className="stat-card-value text-[#34C759]">{formatCurrency(cashflow?.total_income || 0)}</p>
            {includeInvoices && cashflow?.invoices_income > 0 && (
              <p className="text-xs text-[#86868B] mt-1">
                di cui {formatCurrency(cashflow.invoices_income)} da fatture
              </p>
            )}
          </CardContent>
        </Card>
        
        <Card className="stat-card card-hover">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <TrendingDown className="w-5 h-5 text-[#FF3B30]" />
              <p className="stat-card-label">Uscite Totali</p>
            </div>
            <p className="stat-card-value text-[#FF3B30]">{formatCurrency(cashflow?.total_expense || 0)}</p>
            {includeInvoices && cashflow?.invoices_expense > 0 && (
              <p className="text-xs text-[#86868B] mt-1">
                di cui {formatCurrency(cashflow.invoices_expense)} da fatture
              </p>
            )}
          </CardContent>
        </Card>
        
        <Card className="stat-card card-hover">
          <CardContent className="pt-6">
            <p className="stat-card-label">Saldo Fine Periodo</p>
            <p className={`stat-card-value ${
              (cashflow?.final_balance || 0) >= 0 ? 'text-[#34C759]' : 'text-[#FF3B30]'
            }`}>
              {formatCurrency(cashflow?.final_balance || 0)}
            </p>
          </CardContent>
        </Card>
        
        <Card className="stat-card card-hover">
          <CardContent className="pt-6">
            <p className="stat-card-label">Variazione Netta</p>
            <p className={`stat-card-value ${
              ((cashflow?.total_income || 0) - (cashflow?.total_expense || 0)) >= 0 ? 'text-[#34C759]' : 'text-[#FF3B30]'
            }`}>
              {formatCurrency((cashflow?.total_income || 0) - (cashflow?.total_expense || 0))}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Main Chart */}
      <Card className="bg-white rounded-2xl border-0 shadow-[0_2px_12px_rgba(0,0,0,0.04)] mb-8">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-[#1D1D1F]">
            Andamento Saldo {includeInvoices && "(con Fatture)"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[350px]" data-testid="cashflow-main-chart">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={displayData}>
                <defs>
                  <linearGradient id="colorBalance" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#007AFF" stopOpacity={0.2}/>
                    <stop offset="95%" stopColor="#007AFF" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorNegative" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#FF3B30" stopOpacity={0.2}/>
                    <stop offset="95%" stopColor="#FF3B30" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={(value) => new Date(value).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' })}
                  stroke="#86868B"
                  fontSize={12}
                  interval={4}
                />
                <YAxis 
                  tickFormatter={(value) => `€${(value/1000).toFixed(0)}k`}
                  stroke="#86868B"
                  fontSize={12}
                />
                <Tooltip content={<CustomTooltip />} />
                <ReferenceLine y={0} stroke="#FF3B30" strokeDasharray="3 3" />
                <Area 
                  type="monotone" 
                  dataKey="balance" 
                  stroke="#007AFF" 
                  strokeWidth={2}
                  fillOpacity={1} 
                  fill="url(#colorBalance)" 
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Weekly Breakdown with Invoice Separation */}
      <Card className="bg-white rounded-2xl border-0 shadow-[0_2px_12px_rgba(0,0,0,0.04)] mb-8">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-[#1D1D1F]">
            Dettaglio Settimanale
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]" data-testid="weekly-breakdown-chart">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={weeklyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                <XAxis dataKey="week" stroke="#86868B" fontSize={12} />
                <YAxis tickFormatter={(value) => `€${(value/1000).toFixed(0)}k`} stroke="#86868B" fontSize={12} />
                <Tooltip 
                  formatter={(value, name) => {
                    const labels = {
                      income: 'Entrate Movimenti',
                      expense: 'Uscite Movimenti',
                      invoice_income: 'Entrate Fatture',
                      invoice_expense: 'Uscite Fatture',
                      balance: 'Saldo'
                    };
                    return [formatCurrency(value), labels[name] || name];
                  }}
                  contentStyle={{ 
                    background: 'white', 
                    border: 'none', 
                    borderRadius: '12px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                  }}
                />
                <Legend 
                  formatter={(value) => ({
                    income: 'Entrate',
                    expense: 'Uscite',
                    invoice_income: 'Fatture Attive',
                    invoice_expense: 'Fatture Passive',
                    balance: 'Saldo'
                  }[value] || value)}
                />
                <Bar dataKey="income" stackId="income" fill="#34C759" radius={[0, 0, 0, 0]} />
                {includeInvoices && <Bar dataKey="invoice_income" stackId="income" fill="#34C759" fillOpacity={0.5} radius={[4, 4, 0, 0]} />}
                <Bar dataKey="expense" stackId="expense" fill="#FF3B30" radius={[0, 0, 0, 0]} />
                {includeInvoices && <Bar dataKey="invoice_expense" stackId="expense" fill="#FF3B30" fillOpacity={0.5} radius={[4, 4, 0, 0]} />}
                <Line type="monotone" dataKey="balance" stroke="#007AFF" strokeWidth={2} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Upcoming Invoice Payments */}
      {includeInvoices && daysWithInvoices.length > 0 && (
        <Card className="bg-white rounded-2xl border-0 shadow-[0_2px_12px_rgba(0,0,0,0.04)] mb-8">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-[#1D1D1F] flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#007AFF]" />
              Scadenze Fatture nei Prossimi {viewMode} Giorni
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {daysWithInvoices.slice(0, 10).map((day) => (
                <div key={day.date} className="p-4 bg-[#F5F5F7]/50 rounded-xl">
                  <div className="flex items-center justify-between mb-3">
                    <p className="font-semibold text-[#1D1D1F]">
                      {new Date(day.date).toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}
                    </p>
                    <Badge variant="outline" className="text-[#86868B]">
                      {day.invoices.length} fattur{day.invoices.length === 1 ? 'a' : 'e'}
                    </Badge>
                  </div>
                  <div className="space-y-2">
                    {day.invoices.map((inv, i) => (
                      <div key={i} className="flex items-center justify-between py-2 border-b border-[#E5E5E5] last:border-0">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                            inv.type === 'income' ? 'bg-[#34C759]/10' : 'bg-[#FF3B30]/10'
                          }`}>
                            {inv.type === 'income' ? (
                              <FileText className="w-4 h-4 text-[#34C759]" />
                            ) : (
                              <FileInput className="w-4 h-4 text-[#FF3B30]" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-[#1D1D1F]">
                              {inv.type === 'income' ? inv.cliente : inv.fornitore}
                            </p>
                            <p className="text-sm text-[#86868B]">
                              {inv.source === 'fattura_attiva' ? 'Fattura Attiva' : 'Fattura Passiva'} • {inv.numero}
                            </p>
                          </div>
                        </div>
                        <p className={`font-semibold ${
                          inv.type === 'income' ? 'text-[#34C759]' : 'text-[#FF3B30]'
                        }`}>
                          {inv.type === 'income' ? '+' : '-'}{formatCurrency(inv.amount)}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Movements */}
      <Card className="bg-white rounded-2xl border-0 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-[#1D1D1F]">
            Movimenti Manuali Recenti
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {movements.slice(0, 10).map((movement) => (
              <div 
                key={movement.id} 
                className="flex items-center justify-between p-4 bg-[#F5F5F7]/50 rounded-xl hover:bg-[#F5F5F7] transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    movement.type === 'income' ? 'bg-[#34C759]/10' : 'bg-[#FF3B30]/10'
                  }`}>
                    {movement.type === 'income' ? (
                      <ArrowUpRight className="w-5 h-5 text-[#34C759]" />
                    ) : (
                      <ArrowDownRight className="w-5 h-5 text-[#FF3B30]" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-[#1D1D1F]">{movement.description}</p>
                    <p className="text-sm text-[#86868B]">
                      {movement.category} • {new Date(movement.date).toLocaleDateString('it-IT')}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <p className={`font-semibold ${
                    movement.type === 'income' ? 'text-[#34C759]' : 'text-[#FF3B30]'
                  }`}>
                    {movement.type === 'income' ? '+' : '-'}{formatCurrency(movement.amount)}
                  </p>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(movement.id)}
                    className="text-[#86868B] hover:text-[#FF3B30] hover:bg-[#FF3B30]/10"
                    data-testid={`delete-movement-${movement.id}`}
                  >
                    Elimina
                  </Button>
                </div>
              </div>
            ))}
            {movements.length === 0 && (
              <p className="text-center text-[#86868B] py-8">Nessun movimento registrato</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Cashflow;
